import React, { Suspense, useRef, useMemo, useState, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Stars, useTexture, Html } from "@react-three/drei";
import * as THREE from "three";

const TEXTURES = {
  moon: "https://cdn.jsdelivr.net/gh/jeromeetienne/threex.planets@master/images/moonmap1k.jpg",
  mars: "https://cdn.jsdelivr.net/gh/jeromeetienne/threex.planets@master/images/marsmap1k.jpg",
};

// Convert lat/lon (degrees) to XYZ on sphere of radius r
export function latLonToVec3(lat, lon, r = 1.02) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  const x = -r * Math.sin(phi) * Math.cos(theta);
  const z = r * Math.sin(phi) * Math.sin(theta);
  const y = r * Math.cos(phi);
  return [x, y, z];
}

function PlanetSphere({ planet, autoRotate, rotationSpeed = 0.05 }) {
  const meshRef = useRef();
  const texture = useTexture(TEXTURES[planet]);
  useMemo(() => {
    if (texture) {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 8;
    }
  }, [texture]);

  useFrame((_, dt) => {
    if (autoRotate && meshRef.current) meshRef.current.rotation.y += dt * rotationSpeed;
  });

  return (
    <mesh ref={meshRef} castShadow receiveShadow>
      <sphereGeometry args={[1, 96, 96]} />
      <meshStandardMaterial
        map={texture}
        roughness={planet === "mars" ? 0.95 : 0.99}
        metalness={0.05}
      />
    </mesh>
  );
}

function Marker({ position, label, onClick, active }) {
  const [hover, setHover] = useState(false);
  const meshRef = useRef();
  const glowRef = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const pulse = (Math.sin(t * 3) + 1) / 2; // 0..1
    if (meshRef.current) {
      const c = active
        ? new THREE.Color("#D4AF37")
        : new THREE.Color(pulse > 0.5 ? "#22ff88" : "#ffffff");
      meshRef.current.material.color = c;
    }
    if (glowRef.current) {
      glowRef.current.material.opacity = active ? 0.7 : 0.35 + pulse * 0.35;
      const s = 1 + pulse * 0.6;
      glowRef.current.scale.set(s, s, s);
    }
  });
  const handle = (e) => {
    e.stopPropagation();
    onClick && onClick();
  };
  return (
    <group position={position}>
      {/* Invisible enlarged hitbox — improves mobile tap accuracy */}
      <mesh
        onPointerOver={() => setHover(true)}
        onPointerOut={() => setHover(false)}
        onPointerDown={handle}
      >
        <sphereGeometry args={[0.07, 12, 12]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} />
      </mesh>
      {/* Halo */}
      <mesh ref={glowRef}>
        <sphereGeometry args={[0.032, 16, 16]} />
        <meshBasicMaterial color={active ? "#D4AF37" : "#22ff88"} transparent opacity={0.4} depthWrite={false} />
      </mesh>
      {/* Core dot */}
      <mesh ref={meshRef}>
        <sphereGeometry args={[0.019, 16, 16]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
      {(hover || active) && (
        <Html distanceFactor={6} position={[0, 0.05, 0]} center style={{ pointerEvents: "none" }}>
          <div className="px-2 py-1 rounded-md text-[10px] font-mono-space tracking-wide bg-black/80 border border-white/20 text-white whitespace-nowrap">
            {label}
          </div>
        </Html>
      )}
    </group>
  );
}

/**
 * Auto-fits the planet inside the canvas regardless of viewport aspect.
 * Runs once and on every resize.
 */
function FitCamera({ interactive, cameraZ }) {
  const { camera, size } = useThree();
  useEffect(() => {
    const aspect = size.width / Math.max(1, size.height);
    const fovRad = (45 * Math.PI) / 180;
    // Distance so a sphere of radius 1 fits vertically
    const vertDist = 1 / Math.tan(fovRad / 2);
    // Distance so it also fits horizontally
    const horizDist = 1 / (Math.tan(fovRad / 2) * aspect);
    const padding = interactive ? 1.4 : 1.2;
    const targetZ = Math.max(vertDist, horizDist) * padding;
    camera.position.set(0, 0, Math.max(cameraZ, targetZ));
    camera.updateProjectionMatrix();
  }, [camera, size.width, size.height, interactive, cameraZ]);
  return null;
}

/**
 * Planet3D
 *   - `interactive=false` renders a small autorotating planet (hero)
 *   - `interactive=true` renders zoomable planet with markers
 */
export default function Planet3D({
  planet = "moon",
  interactive = false,
  markers = [],
  onMarkerClick,
  activeMarkerId,
  height = "100%",
  autoRotate = true,
  rotationSpeed = 0.05,
  cameraZ = 3.2,
}) {
  return (
    <div
      style={{ width: "100%", height, pointerEvents: interactive ? "auto" : "none" }}
      className={interactive ? "planet-canvas planet-canvas-interactive" : "planet-canvas planet-canvas-static"}
      data-testid={`planet-canvas-${planet}`}
    >
      <Canvas camera={{ position: [0, 0, cameraZ], fov: 45 }} dpr={[1, 2]} shadows gl={{ alpha: true }} style={{ background: "transparent", touchAction: interactive ? "none" : "pan-y" }}>
        <FitCamera interactive={interactive} cameraZ={cameraZ} />
        <ambientLight intensity={0.35} />
        <directionalLight position={[5, 3, 5]} intensity={2.2} castShadow />
        <directionalLight position={[-4, -2, -3]} intensity={0.15} color="#5b6ee1" />
        {interactive && <Stars radius={80} depth={40} count={4000} factor={4} fade speed={0.5} />}
        <Suspense fallback={null}>
          <PlanetSphere planet={planet} autoRotate={autoRotate && !interactive ? true : false} rotationSpeed={rotationSpeed} />
          {markers.map((m) => (
            <Marker
              key={m.id}
              position={latLonToVec3(m.lat, m.lon, 1.015)}
              label={m.label}
              onClick={() => onMarkerClick && onMarkerClick(m)}
              active={activeMarkerId === m.id}
            />
          ))}
        </Suspense>
        <OrbitControls
          enablePan={false}
          enableRotate={interactive}
          enableZoom={interactive}
          autoRotate={!interactive && autoRotate}
          autoRotateSpeed={rotationSpeed * 12}
          minDistance={1.4}
          maxDistance={6}
          rotateSpeed={0.7}
        />
      </Canvas>
    </div>
  );
}
